import { useEffect, useState } from 'react';
import { ButtonGlow, ButtonGradient } from '@g17eco/atoms/button';
import { ProgressBar } from './ProgressBar';
import { SettingsModal } from './SettingsModal';
import { useToggle } from '@hooks/useToggle';
import { Button } from 'reactstrap';
import { VerticalDivider } from '@g17eco/atoms/divider';
import {
  AIAutoAnswerSettings,
  useAiAutoAnswerSurveyMutation,
  useAiAutoAnswerSurveyPauseMutation,
  useAiAutoAnswerSurveyResumeMutation,
  useAiAutoAnswerSurveyAbortMutation,
  useGetAIAutoAnswerSurveyQuery,
  SettingOption
} from '@api/ai';
import { canRetryJob, hasLogs, isFinishedJob, isPausedJob, isPendingJob, isProcessingJob } from '@utils/background-job';
import { TaskStatus, TaskType } from '@g17eco/types/background-jobs';
import { ProgressStats } from '@utils/survey';
import { useSiteAlert } from '@hooks/useSiteAlert';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { SurveyActionData } from '../../types/surveyAction';
import { BlockingLoader } from '../../atoms/loader';
import {
  useListJobsBySurveyQuery,
  useSubmitAutoAnswerBatchMutation,
  usePauseBatchMutation,
  useResumeBatchMutation,
  useAbortBatchMutation,
  BatchJob
} from '../../api/ai-supervisor';
import { AIAutoAnswerSurveyJobPlain } from '../../types/ai-auto-answer-job';

interface Props {
  survey: Pick<SurveyActionData, '_id' | 'completedDate'>;
  initiativeId: string;
  progressStats: ProgressStats;
  canAccessAIDocumentLibrary: boolean;
  toggleStreaming: () => void;
  handleViewProgress?: (batchId?: string) => void;
}

// Helper functions for batch job status
const isBatchProcessing = (batchJob: BatchJob | undefined) => {
  return batchJob?.status === 'processing';
};

const isBatchPaused = (batchJob: BatchJob | undefined) => {
  return batchJob?.status === 'paused';
};

const isBatchFinished = (batchJob: BatchJob | undefined) => {
  return batchJob?.status === 'completed' || batchJob?.status === 'failed' || batchJob?.status === 'cancelled';
};

// Determine which job is most recent
const getMostRecentJob = (legacyJob: AIAutoAnswerSurveyJobPlain | undefined, batchJob: BatchJob | undefined): 'legacy' | 'batch' | null => {
  if (!legacyJob && !batchJob) return null;
  if (!legacyJob) return 'batch';
  if (!batchJob) return 'legacy';

  // Compare dates - batch jobs have createdAt, legacy jobs have updated (which is most recent activity)
  const batchDate = new Date(batchJob.createdAt).getTime();
  const legacyDate = new Date(legacyJob.updated).getTime();

  return batchDate > legacyDate ? 'batch' : 'legacy';
};

export const AIAutoAnswerButtons = ({ initiativeId, survey, progressStats, canAccessAIDocumentLibrary, toggleStreaming, handleViewProgress }: Props) => {
  const { addSiteError, addSiteAlert } = useSiteAlert();

  const [openSettings, toggleSettings, setOpenSettings] = useToggle();
  const [isInitiating, setIsInitiating] = useState(false);
  const [skipPolling, setSkipPolling] = useState(false);

  const { data: batchJobsData } = useListJobsBySurveyQuery(
    {
      initiativeId,
      surveyId: survey._id,
    }
  );
  // Get the most recent batch (first in array)
  const latestBatchJob = batchJobsData?.jobs?.[0];

  const { data, isSuccess, error } = useGetAIAutoAnswerSurveyQuery(
    { initiativeId, surveyId: survey._id },
    { skip: skipPolling, pollingInterval: 10000 },
  );

  const job = isSuccess ? data.job : undefined;

  // Determine which job type is active (most recent)
  const activeJobType = getMostRecentJob(job, latestBatchJob);
  const isBatchJob = activeJobType === 'batch';
  const isLegacyJob = activeJobType === 'legacy';

  // Unified status checks that work for both job types
  const processingJob = isBatchJob
    ? isBatchProcessing(latestBatchJob)
    : job && isProcessingJob(job);

  const pausedJob = isBatchJob
    ? isBatchPaused(latestBatchJob)
    : job && isPausedJob(job);

  const pendingJob = isLegacyJob
    ? job && isPendingJob(job)
    : false;

  const finishedJob = isBatchJob
    ? isBatchFinished(latestBatchJob)
    : job && isFinishedJob(job);

  const canRetry = isLegacyJob && job ? canRetryJob(job) : false;

  const showAutoAnswerProgress = isBatchJob
    ? Boolean(latestBatchJob)
    : job && hasLogs(job);

  const [autoAnswerSurvey, { isLoading: creatingJob }] = useAiAutoAnswerSurveyMutation();
  const [aiAgentAutoAnswerService, { isLoading: submittingAIAgentMode }] = useSubmitAutoAnswerBatchMutation();
  const [autoAnswerSurveyPause] = useAiAutoAnswerSurveyPauseMutation();
  const [autoAnswerSurveyResume] = useAiAutoAnswerSurveyResumeMutation();
  const [autoAnswerSurveyAbort] = useAiAutoAnswerSurveyAbortMutation();

  // Batch job mutations
  const [pauseBatch] = usePauseBatchMutation();
  const [resumeBatch] = useResumeBatchMutation();
  const [abortBatch] = useAbortBatchMutation();

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    if (data.job?._id) {
      setIsInitiating(false);
    }
    // Skip polling when not found job or job is finished
    if (!data?.job || isFinishedJob(data.job)) {
      setSkipPolling(true);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (error && error.message) {
      addSiteError(error.message);
    }
  }, [error, addSiteError]);

  if (survey.completedDate) {
    return null;
  }

  const handleAutoAnswer = (settings: AIAutoAnswerSettings) => {
    // Trigger polling for processing data
    setSkipPolling(false);
    setIsInitiating(true);
    if (settings[SettingOption.AIAgentMode]) {
      // Convert settings to new API format
      // TODO: Get actual questionIds from survey selection
      aiAgentAutoAnswerService({
        initiativeId,
        surveyId: survey._id,
        data: {
          documentIds: settings[SettingOption.DocumentLibrary],
          model: settings[SettingOption.AIAgentModeOptions]?.model,
        }
      })
        .unwrap()
        .then((response) => {
          handleViewProgress?.(response.batchId);
        })
        .catch((e) => addSiteError(e));
    } else {
      autoAnswerSurvey({ initiativeId, surveyId: survey._id, settings })
        .unwrap()
        .then(() => toggleStreaming())
        .catch((e) => addSiteError(e));
    }
    setOpenSettings(false);
  };

  const handlePause = () => {
    const handler = isBatchJob && latestBatchJob ?
      pauseBatch({ initiativeId, surveyId: survey._id, batchId: latestBatchJob.batchId }) :
      autoAnswerSurveyPause({ initiativeId, surveyId: survey._id });

    handler.unwrap()
      .then(() => {
        addSiteAlert({
          content: 'Auto answering paused successfully',
        });
      })
      .catch((e) => addSiteError(e));
  };

  const handleResume = () => {
    const handler = isBatchJob && latestBatchJob ?
      resumeBatch({ initiativeId, surveyId: survey._id, batchId: latestBatchJob.batchId }) :
      autoAnswerSurveyResume({ initiativeId, surveyId: survey._id });

    handler.unwrap()
      .then(() => {
        addSiteAlert({
          content: 'Auto answering resumed successfully',
        });
      })
      .catch((e) => addSiteError(e));
  };

  const handleAbort = () => {
    const handler = isBatchJob && latestBatchJob ?
      abortBatch({ initiativeId, surveyId: survey._id, batchId: latestBatchJob.batchId }) :
      autoAnswerSurveyAbort({ initiativeId, surveyId: survey._id });

    handler.unwrap()
      .then(() => {
        addSiteAlert({
          content: 'Auto answering aborted successfully',
        });
      })
      .catch((e) => addSiteError(e));
  };

  const handleViewAutoAnswerProgress = () => {
    if (isBatchJob) {
      handleViewProgress?.();
    } else {
      toggleStreaming();
    }
  };

  const getProcessingStatus = () => {
    if (finishedJob || canRetry) {
      return false;
    }
    return creatingJob || submittingAIAgentMode || Boolean(processingJob);
  };

  const isPaused = !finishedJob && (pausedJob || pendingJob);
  const isProcessing = getProcessingStatus();

  const processedUtrvs =
    job?.tasks.filter(
      (task) =>
        task.type === TaskType.AIAutoAnswerProcess && [TaskStatus.Completed, TaskStatus.Error].includes(task.status),
    ).length ?? 0;

  const numOfJobProcessUtrvs = job?.tasks.filter((task) => task.type === TaskType.AIAutoAnswerProcess).length ?? 0;
  const totalUtrvs = Math.max(numOfJobProcessUtrvs, progressStats.created);

  const glowOffset = { marginTop: '3px'};
  return (
    <>
    {isInitiating ? <BlockingLoader /> : null}
    <div className='w-100 d-flex align-items-center mb-2'>
      {isPaused ? (
        <>
          <ButtonGradient style={glowOffset} onClick={handleResume}>
            <i className='fal fa-play mr-2' />
            Continue Auto Answering...
          </ButtonGradient>
          <Button color='danger' style={glowOffset} outline onClick={handleAbort} className='ms-2'>
            <i className='fal fa-times text-danger me-2' />
            Abort
          </Button>
        </>
      ) : isProcessing ? (
        <>
          <ButtonGlow color='secondary' disabled>
            <i className='fal fa-stars me-3' />
            Auto Answering...
          </ButtonGlow>
          <Button color='secondary' style={glowOffset} onClick={handlePause} className='ms-2'>
            <i className='fal fa-pause text-ThemeIconSecondary me-2' />
            Pause
          </Button>
        </>
      ) :
      (
        <ButtonGradient style={glowOffset} onClick={toggleSettings}>
          <i className='fal fa-stars mr-2' />
          Auto Answer
        </ButtonGradient>
      )}
      {showAutoAnswerProgress ? <Button color='secondary' style={glowOffset} onClick={handleViewAutoAnswerProgress} className='ms-2'>
        View Auto-Answer Progress
      </Button> : null}
      <Button
        data-testid='auto-answer-btn'
        color='transparent'
        className='ms-2 px-1'
        onClick={toggleSettings}
        disabled={isProcessing}
      >
        <i className='fal fa-gear text-ThemeIconSecondary' />
      </Button>
      <FeatureStability stability='internal' className='mx-1' />
      <VerticalDivider className='lh-lg mx-1' />
      <ProgressBar
        classes={{ wrapper: 'flex-grow-1', progressBar: 'processing' }}
        job={job}
        isProcessing={isProcessing}
        processedUtrvs={processedUtrvs}
        totalUtrvs={totalUtrvs}
      />
      <SettingsModal
        key={openSettings ? 'open' : 'closed'}
        isOpen={openSettings}
        toggle={toggleSettings}
        initiativeId={initiativeId}
        handleSubmit={handleAutoAnswer}
        canAccessAIDocumentLibrary={canAccessAIDocumentLibrary}
      />
    </div>
    </>
  );
};
